/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {noop} from '@benchling/js-utils';
import moment from 'moment';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import DocumentStore from 'notebook/documents/DocumentStore';
import EntryExportMetadata from 'notebook/entries/EntryExportMetadata';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import PrintableWrapper from 'notebook/mediocre/print/PrintableWrapper';
import {EditorPropertiesContext, SectionConfigurationContext} from 'notebook/notes/core-ui/NoteContext';
import NoteEditorContainer from 'notebook/notes/core-ui/NoteEditorContainer';
// @ts-expect-error
import ProtocolEditor from 'notebook/protocols/editor/Editor';
import WorksheetView, {WorksheetViewProps} from 'notebook/worksheets/workspace/WorksheetView';
import RequestEntryTable from 'requests/RequestEntryTable';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';

import EntryExportConfig from './EntryExportConfig';
import {
  editorPropertiesContextForPrintView,
  getExportConfigForMetadataSection,
  getPrinterFriendlySectionConfigurationContext,
} from './PrinterFriendlyViewUtils';

class EntryPrinterFriendlyViewConnector extends DataConnector<Props, EnhancedProps | null> {
  shouldSubscribeToRedux = false;
  stores = [EntryStore];

  enhanceProps(): EnhancedProps | null {
    const {entry, cidIdMap} = this.entryStore().getEditorState(this.props.id);
    const note = entry?.note;
    return {model: entry, note, cidIdMap};
  }
}

class WorksheetPrinterFriendlyViewConnector extends DataConnector<Props, WorksheetViewProps | null> {
  shouldSubscribeToRedux = false;
  stores = [DocumentStore];

  enhanceProps(): WorksheetViewProps | null {
    return {
      id: this.props.id,
      viewer: true,
    };
  }
}

class TemplatePrinterFriendlyViewConnector extends DataConnector<Props, EnhancedProps | null> {
  shouldSubscribeToRedux = false;
  stores = [TemplateStore];

  enhanceProps(): EnhancedProps | null {
    const {template, cidIdMap} = this.templateStore().getEditorState(this.props.id);
    const note = template?.templateVersion.entryVersion.note;
    return {model: template, note, cidIdMap};
  }
}

class SubtemplatePrinterFriendlyViewConnector extends DataConnector<Props, EnhancedProps | null> {
  shouldSubscribeToRedux = false;
  stores = [SubtemplateStore];

  enhanceProps(): EnhancedProps | null {
    const {subtemplate, cidIdMap} = this.subtemplateStore().getEditorState(this.props.id);
    const note = subtemplate?.subtemplateVersion.noteVersion;
    return {model: subtemplate, note, cidIdMap};
  }
}

interface Props {
  id: string;
  dateSlice?: any;
  showNote?: boolean;
  showProtocols: Array<any>;
  exportConfig?: EntryExportConfig;
  indexSlice?: {
    start: number;
    end: number;
  };
  showTimestamps?: boolean;
  reviewChangeDate?: moment.Moment;
}

interface EnhancedProps {
  model: any;
  note: any;
  cidIdMap: any;
}

class PrinterFriendlyView extends React.Component<Props & EnhancedProps> {
  renderProtocols = (): React.ReactNode => {
    if (!this.props.model.entryProtocols) {
      // Subtemplates don't have protocols
      return [];
    }
    const result = [];
    const shouldRender = (p: any): boolean =>
      this.props.showProtocols.includes(this.props.cidIdMap[p.cid]) && p.externalFile == null;
    for (const protocol of this.props.model.entryProtocols) {
      if (shouldRender(protocol)) {
        result.push(
          <div key={protocol.cid} className="printable-page-container">
            <ProtocolEditor
              fileId={this.props.id}
              protocol={protocol}
              locked={true}
              storeState={noop}
              restoreState={noop}
              requestSelection={noop}
              viewer={true}
              showPrintMargins={false}
            />
          </div>
        );
      }
    }
    return result;
  };

  renderEditor = (): React.ReactNode => {
    if (this.props.model == null) {
      return <HorizontalThrobber />;
    }

    let {exportConfig} = this.props;
    // Show the title by default unless explicitly disabled in the export config
    const showTitle = exportConfig?.showEntryTitle ?? true;

    // Show the first section header unless the export config is asking for us not to do so.
    // To hide the section we mark the preamble as being true in `getPrinterFriendlySectionConfigurationContext`.
    // This then causes the same behavior that we have for Documents where we don't show the first SectionHeader.
    const showFirstSectionHeader = exportConfig?.showFirstSectionHeader ?? true;
    const sectionConfiguration = getPrinterFriendlySectionConfigurationContext(!showFirstSectionHeader);

    // If the metadata section in print preview is enabled, populate/update the related exportConfig props.
    if (BenchlingConfig.ELN_RENDER_PRINT_PREVIEW_METADATA) {
      exportConfig = getExportConfigForMetadataSection(exportConfig);
    }

    let parentType: string = '';
    let getIdFromCid;
    let beforeEntry;
    const {humanId, name, permissions} = this.props.model;
    if (APIUtil.isEntry(this.props.id)) {
      if (showTitle) {
        beforeEntry = (
          <div>
            {exportConfig && (
              <EntryExportMetadata
                entry={this.props.model}
                exportConfig={exportConfig}
                reviewChangeDate={this.props.reviewChangeDate}
              />
            )}
            <RequestEntryTable entryId={this.props.id} locked={true} viewer={true} />
          </div>
        );
      }
      parentType = 'Entry';
      getIdFromCid = (cid: any) => EntryStore.getIdFromCid(this.props.id, cid);
    } else if (APIUtil.isEntryTemplate(this.props.id)) {
      parentType = 'Template';
      getIdFromCid = (cid: any) => TemplateStore.getIdFromCid(this.props.id, cid);
    } else if (APIUtil.isSubtemplate(this.props.id)) {
      parentType = 'Subtemplate';
      getIdFromCid = (cid: any) => SubtemplateStore.getIdFromCid(this.props.id, cid);
    } else {
      return null;
    }

    return (
      <div>
        <SectionConfigurationContext.Provider value={sectionConfiguration}>
          <EditorPropertiesContext.Provider
            value={editorPropertiesContextForPrintView(this.props.id, Boolean(exportConfig))}
          >
            {this.props.showNote ? (
              <div className="printable-page-container">
                <NoteEditorContainer
                  hideTitle={!showTitle}
                  beforeEntry={beforeEntry}
                  fileId={this.props.id}
                  humanId={humanId}
                  note={this.props.note}
                  parentType={parentType}
                  title={name}
                  permissions={permissions}
                  // Layout for print
                  dateSlice={this.props.dateSlice}
                  indexSlice={this.props.indexSlice}
                  disableFormatting={true}
                  locked={true}
                  preview={{showOpenLink: false}}
                  viewer={true}
                  getIdFromCid={getIdFromCid}
                  // No-op since printable view is non-interactive
                  registerObjects={noop}
                  restoreState={() => false}
                  storeState={noop}
                  undoStoreState={noop}
                  undoState={noop}
                  redoState={noop}
                  // Display Options
                  printLayout={false} // UI paginated layout
                  fullScreen={false} // Follows normal page flow (not absolute positioning)
                  showPrintMargins={false}
                  spellCheck={false}
                  showTableOfContents={false}
                  showTimestamps={this.props.showTimestamps}
                />
              </div>
            ) : undefined}
            {this.renderProtocols()}
          </EditorPropertiesContext.Provider>
        </SectionConfigurationContext.Provider>
      </div>
    );
  };

  render(): JSX.Element {
    return (
      <PrintableWrapper title={this.props.model?.name} shouldPadForTimestamps={this.props.showTimestamps}>
        {this.renderEditor()}
      </PrintableWrapper>
    );
  }
}

export const EntryPrinterFriendlyView = EntryPrinterFriendlyViewConnector.connect(PrinterFriendlyView);
export const WorksheetPrinterFriendlyView = WorksheetPrinterFriendlyViewConnector.connect(WorksheetView);
export const TemplatePrinterFriendlyView = TemplatePrinterFriendlyViewConnector.connect(PrinterFriendlyView);
export const SubtemplatePrinterFriendlyView =
  SubtemplatePrinterFriendlyViewConnector.connect(PrinterFriendlyView);
